import {clsx} from 'clsx'
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Button, Spinner, VisuallyHidden} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {CopilotResultRenderIdentity} from '@github-ui/copilot-browser-latency/copilot-result-presentation'
import {useCopilotResultElement} from '@github-ui/copilot-browser-latency/use-copilot-result-presentation'
import type {ToolCallState} from '@microsoft/agent-host-protocol'
import {AnimatedCollapse} from '../../../AnimatedCollapse'
import {ahpToolTimingKey, finishAhpToolTimings} from '../../../../ahp/use-ahp-tool-timing'
import {useSessionContext} from '../../../../contexts/SessionContext'
import {useSessionLogsContext} from '../../Context'
import type {ToolDisclosureMemory} from '../../tool/ToolPrimitives'
import {
  summarizeToolCallGroup,
  type ResponsePartEntry,
  type ResponseRun,
  type ResponseSection,
  type ToolCallGroup,
} from '../tool-call-groups'
import {toolPhase} from './AhpToolCall'
import turnStyles from '../AhpTurnView.module.css'
import styles from './AhpToolCallGroup.module.css'

const NO_PRESENTATION = {turn_id: ''}
const GLOBAL_EXPAND_REPLAY_INSTANCE = {}
let globalExpandReplayPending = false

function scheduleGlobalExpandReplay() {
  if (globalExpandReplayPending) return
  globalExpandReplayPending = true
  queueMicrotask(() => {
    globalExpandReplayPending = false
    document.dispatchEvent(
      new CustomEvent('mission-control-tool-global-toggle', {
        detail: {expand: true, excludeInstance: GLOBAL_EXPAND_REPLAY_INSTANCE},
      }),
    )
  })
}

export type RegisterGroupTrigger = (items: readonly ResponsePartEntry[], trigger: HTMLButtonElement) => () => void

type ToolCallDisclosureGroup = Pick<ToolCallGroup, 'items' | 'toolCalls'>

function rememberedOpen(
  toolCalls: ToolCallDisclosureGroup['toolCalls'],
  disclosureMemoryFor: ((key: string) => ToolDisclosureMemory) | undefined,
) {
  if (!disclosureMemoryFor) return undefined
  let remembered: boolean | undefined
  for (const {toolCallId} of toolCalls) {
    const open = disclosureMemoryFor(toolCallId).read()
    if (open) return true
    if (open === false) remembered = false
  }
  return remembered
}

function useToolCallDisclosure({
  group,
  defaultExpanded,
  disclosureMemoryFor,
  registerGroupTrigger,
  telemetryTarget,
  active = true,
}: {
  group: ToolCallDisclosureGroup
  defaultExpanded: boolean
  active?: boolean
  disclosureMemoryFor?: (key: string) => ToolDisclosureMemory
  registerGroupTrigger: RegisterGroupTrigger
  telemetryTarget: 'TOOL_CALL_GROUP' | 'TOOL_CALL_SUMMARY'
}) {
  const [explicitOpen, setExplicitOpen] = useState(() => rememberedOpen(group.toolCalls, disclosureMemoryFor))
  const open = explicitOpen ?? defaultExpanded
  const [hasBeenOpen, setHasBeenOpen] = useState(open)
  if (open && !hasBeenOpen) setHasBeenOpen(true)
  const openRef = useRef(open)
  const activeRef = useRef(active)
  const instanceRef = useRef({})
  const pendingGlobalExpandRef = useRef(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const {onToolExpand} = useSessionLogsContext()

  useLayoutEffect(() => {
    openRef.current = open
  }, [open])

  useLayoutEffect(() => {
    activeRef.current = active
  }, [active])

  const attachTrigger = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node
      if (!node) return
      const unregister = registerGroupTrigger(group.items, node)
      return () => {
        unregister()
        triggerRef.current = null
      }
    },
    [group.items, registerGroupTrigger],
  )
  const focusTriggerIfNeeded = useCallback(() => {
    const trigger = triggerRef.current
    const activeElement = document.activeElement
    const root = trigger?.closest('[data-ahp-tool-call-group]')
    if (!trigger || !root || !activeElement || activeElement === trigger) return
    let content = activeElement.closest('[data-ahp-disclosure-content]')
    while (content && content.closest('[data-ahp-tool-call-group]') !== root) {
      content = content.parentElement?.closest('[data-ahp-disclosure-content]') ?? null
    }
    if (content) trigger.focus()
  }, [])

  useEffect(() => {
    const handleGlobalToggle = (event: Event) => {
      const {expand, excludeInstance} = (event as CustomEvent<{expand: boolean; excludeInstance: object}>).detail
      if (excludeInstance === instanceRef.current || !activeRef.current) return
      if (expand === openRef.current) {
        setExplicitOpen(expand)
        return
      }
      if (!expand) focusTriggerIfNeeded()
      openRef.current = expand
      pendingGlobalExpandRef.current = expand
      setExplicitOpen(expand)
    }
    document.addEventListener('mission-control-tool-global-toggle', handleGlobalToggle)
    return () => document.removeEventListener('mission-control-tool-global-toggle', handleGlobalToggle)
  }, [focusTriggerIfNeeded])

  const rememberedToolCallsRef = useRef(group.toolCalls)
  useLayoutEffect(() => {
    if (!disclosureMemoryFor) return
    const membersChanged = rememberedToolCallsRef.current !== group.toolCalls
    rememberedToolCallsRef.current = group.toolCalls
    if (
      membersChanged &&
      !openRef.current &&
      group.toolCalls.some(({toolCallId}) => disclosureMemoryFor(toolCallId).read())
    ) {
      openRef.current = true
      setExplicitOpen(true)
      return
    }
    if (explicitOpen === undefined) return
    for (const {toolCallId} of group.toolCalls) disclosureMemoryFor(toolCallId).write(explicitOpen)
  }, [disclosureMemoryFor, explicitOpen, group.toolCalls])

  useEffect(() => {
    if (!pendingGlobalExpandRef.current) return
    pendingGlobalExpandRef.current = false
    scheduleGlobalExpandReplay()
  }, [open])

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    const expand = !openRef.current
    const modifierClick = event.getModifierState('Alt') || event.getModifierState('Meta')
    if (!expand) focusTriggerIfNeeded()
    openRef.current = expand
    pendingGlobalExpandRef.current = modifierClick && expand
    setExplicitOpen(expand)
    if (modifierClick) {
      event.preventDefault()
      event.stopPropagation()
      document.dispatchEvent(
        new CustomEvent('mission-control-tool-global-toggle', {
          detail: {expand, excludeInstance: instanceRef.current},
        }),
      )
    }
    onToolExpand?.(expand)
    sendEvent('agent_sessions.task_view', {
      target: telemetryTarget,
      action: expand ? 'EXPAND' : 'COLLAPSE',
      tool_count: group.toolCalls.length,
    })
  }

  return {open, keepMounted: hasBeenOpen && explicitOpen === undefined, attachTrigger, onClick}
}

function concealedWhen(concealed: boolean) {
  return concealed ? {'data-ahp-concealed': ''} : {}
}

function useSettledToolTimings(toolCalls: ToolCallDisclosureGroup['toolCalls'], chatResource: string | undefined) {
  const {session} = useSessionContext()
  useLayoutEffect(() => {
    finishAhpToolTimings(
      toolCalls
        .filter(toolCall => toolPhase(toolCall.status) === 'done')
        .map(toolCall => ahpToolTimingKey(session.task_id, chatResource, toolCall.toolCallId)),
      Date.now(),
    )
  }, [chatResource, toolCalls, session.task_id])
}

function useTerminalElement(presentation: CopilotResultRenderIdentity | undefined, readOnlyHistory: boolean) {
  return useCopilotResultElement(
    presentation ?? NO_PRESENTATION,
    'terminal',
    !readOnlyHistory && Boolean(presentation?.terminal_state && presentation.terminal_state !== 'error'),
  )
}

function DisclosureTrigger({
  open,
  attachTrigger,
  onClick,
  toolCalls,
  inProgress = false,
  summary = false,
}: {
  open: boolean
  attachTrigger: (node: HTMLButtonElement | null) => (() => void) | undefined
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  toolCalls: ToolCallDisclosureGroup['toolCalls']
  inProgress?: boolean
  summary?: boolean
}) {
  return (
    <Button
      type="button"
      variant="invisible"
      labelWrap
      alignContent="start"
      className={styles.Trigger}
      leadingVisual={<ChevronRightIcon aria-hidden="true" className={styles.ToggleIcon} data-expanded={open} />}
      trailingVisual={inProgress ? <Spinner size="small" srText="In progress" /> : undefined}
      aria-expanded={open}
      ref={attachTrigger}
      onClick={onClick}
    >
      <span>{summarizeToolCallGroup(toolCalls)}</span>
      {summary ? <VisuallyHidden>, intermediate steps</VisuallyHidden> : null}
    </Button>
  )
}

type DisclosureProps = {
  chatResource?: string
  presentation?: CopilotResultRenderIdentity
  readOnlyHistory: boolean
  registerGroupTrigger: RegisterGroupTrigger
  children: ReactNode
}

export const AhpToolCallGroup = memo(function AhpToolCallGroupComponent({
  group,
  defaultExpanded = false,
  chatResource,
  presentation,
  readOnlyHistory,
  registerGroupTrigger,
  disclosureMemoryFor,
  children,
}: DisclosureProps & {
  group: ToolCallGroup
  defaultExpanded?: boolean
  disclosureMemoryFor?: (key: string) => ToolDisclosureMemory
}) {
  const {open, keepMounted, attachTrigger, onClick} = useToolCallDisclosure({
    group,
    defaultExpanded,
    disclosureMemoryFor,
    registerGroupTrigger,
    telemetryTarget: 'TOOL_CALL_GROUP',
  })
  useSettledToolTimings(group.toolCalls, chatResource)
  const terminalRef = useTerminalElement(presentation, readOnlyHistory)

  return (
    <div className={turnStyles.toolPart} ref={terminalRef} data-ahp-tool-call-group="">
      <DisclosureTrigger open={open} attachTrigger={attachTrigger} onClick={onClick} toolCalls={group.toolCalls} />
      <AnimatedCollapse open={open} keepMounted={keepMounted}>
        <div className={styles.Content} data-ahp-disclosure-content="" {...concealedWhen(!open)}>
          {children}
        </div>
      </AnimatedCollapse>
    </div>
  )
})

function isSettledToolCall(toolCall: ToolCallState) {
  return toolPhase(toolCall.status) === 'done'
}

function settleLiveRuns(
  pending: ReadonlyMap<string, ReadonlySet<string>>,
  runs: readonly ResponseRun[],
  toolCalls: readonly ToolCallState[],
) {
  const idsInRuns = new Set(runs.flatMap(run => run.toolCalls.map(toolCall => toolCall.toolCallId)))
  const trackedRuns = new Map<string, ReadonlySet<string>>()
  for (const [runId, ids] of pending) trackedRuns.set(runId, new Set([...ids].filter(id => !idsInRuns.has(id))))
  for (const run of runs) {
    trackedRuns.set(run.id, new Set([...(trackedRuns.get(run.id) ?? []), ...run.toolCalls.map(tc => tc.toolCallId)]))
  }
  const positions = new Map(toolCalls.map((toolCall, index) => [toolCall.toolCallId, {toolCall, index}]))
  const next = new Map<string, ReadonlySet<string>>()
  const completed: string[] = []
  const pendingIds = new Set([...pending.values()].flatMap(ids => [...ids]))
  for (const [runId, ids] of trackedRuns) {
    const tracked = [...ids]
      .flatMap(id => positions.get(id) ?? [])
      .sort((a, b) => a.index - b.index)
      .map(entry => entry.toolCall)
    if (tracked.length === 0) continue
    if (!tracked.every(isSettledToolCall)) next.set(runId, new Set(tracked.map(toolCall => toolCall.toolCallId)))
    else if (pending.has(runId) || tracked.some(toolCall => pendingIds.has(toolCall.toolCallId))) {
      completed.push(`${summarizeToolCallGroup(tracked)} complete`)
    }
  }
  return {next, completed}
}

const NO_PENDING_RUNS: ReadonlyMap<string, ReadonlySet<string>> = new Map()

export function useLiveRunCompletionAnnouncements(
  runs: readonly ResponseRun[],
  toolCalls: readonly ToolCallState[],
  enabled: boolean,
) {
  const pendingRunsRef = useRef(NO_PENDING_RUNS)
  useEffect(() => {
    if (!enabled) {
      pendingRunsRef.current = NO_PENDING_RUNS
      return
    }
    const {next, completed} = settleLiveRuns(pendingRunsRef.current, runs, toolCalls)
    pendingRunsRef.current = next
    for (const message of completed) announce(message, {assertive: false})
  }, [enabled, runs, toolCalls])
}

export function AhpToolCallSummary({
  summary,
  collapsed,
  chatResource,
  presentation,
  readOnlyHistory,
  registerGroupTrigger,
  children,
}: DisclosureProps & {summary: ToolCallDisclosureGroup; collapsed: boolean}) {
  const {open, attachTrigger, onClick} = useToolCallDisclosure({
    group: summary,
    defaultExpanded: false,
    registerGroupTrigger,
    telemetryTarget: 'TOOL_CALL_SUMMARY',
    active: collapsed,
  })
  useSettledToolTimings(summary.toolCalls, chatResource)
  const terminalRef = useTerminalElement(presentation, readOnlyHistory)
  const inProgress = collapsed && !summary.toolCalls.every(isSettledToolCall)

  return (
    <div
      className={collapsed ? turnStyles.toolPart : styles.Segment}
      ref={terminalRef}
      data-ahp-tool-call-group=""
      data-ahp-tool-call-segment=""
    >
      <AnimatedCollapse open={collapsed}>
        <div {...concealedWhen(!collapsed)}>
          <DisclosureTrigger
            open={open}
            attachTrigger={attachTrigger}
            onClick={onClick}
            toolCalls={summary.toolCalls}
            inProgress={inProgress}
            summary
          />
        </div>
      </AnimatedCollapse>
      <AnimatedCollapse open={!collapsed || open} keepMounted unclipWhenOpen>
        <div
          className={collapsed ? clsx(styles.Content, styles.SummaryContent) : styles.Flat}
          data-ahp-disclosure-content={collapsed ? '' : undefined}
          {...concealedWhen(collapsed && !open)}
        >
          {children}
        </div>
      </AnimatedCollapse>
    </div>
  )
}

export type ResponseSectionSlot = {
  key: string
  focusKey?: string
  hideable: boolean
  visible: boolean
  node: ReactNode
}

export function AhpResponseSection({
  section,
  slots,
  collapsed,
  chatResource,
  presentation,
  readOnlyHistory,
  registerGroupTrigger,
}: Omit<DisclosureProps, 'children'> & {section: ResponseSection; slots: ResponseSectionSlot[]; collapsed: boolean}) {
  const disclosureGroup = useMemo(() => ({items: section.prefix, toolCalls: section.toolCalls}), [section])
  const {open, attachTrigger, onClick} = useToolCallDisclosure({
    group: disclosureGroup,
    defaultExpanded: false,
    registerGroupTrigger,
    telemetryTarget: 'TOOL_CALL_SUMMARY',
    active: collapsed,
  })
  const sectionToolCalls = useMemo(
    () =>
      section.items.flatMap(item =>
        (item.kind === 'run' ? item.items : [item]).flatMap(({part}) =>
          part.kind === 'toolCall' ? [part.toolCall] : [],
        ),
      ),
    [section],
  )
  useSettledToolTimings(sectionToolCalls, chatResource)
  const terminalRef = useTerminalElement(presentation, readOnlyHistory)

  return (
    <div className={styles.Section} ref={terminalRef} data-ahp-tool-call-group="" data-ahp-tool-call-segment="">
      <AnimatedCollapse open={collapsed}>
        <div className={clsx(styles.SectionSlot, turnStyles.toolPart)} {...concealedWhen(!collapsed)}>
          <DisclosureTrigger
            open={open}
            attachTrigger={attachTrigger}
            onClick={onClick}
            toolCalls={section.toolCalls}
            summary
          />
        </div>
      </AnimatedCollapse>
      {slots.map(slot => {
        const nested = collapsed && slot.hideable
        return (
          <AnimatedCollapse key={slot.key} open={!nested || open} keepMounted unclipWhenOpen>
            <div
              className={!slot.visible ? styles.UnspacedSlot : nested ? styles.NestedSectionSlot : styles.SectionSlot}
              data-ahp-part-key={slot.focusKey}
              data-ahp-disclosure-content={nested ? '' : undefined}
              {...concealedWhen(nested && !open)}
            >
              {slot.node}
            </div>
          </AnimatedCollapse>
        )
      })}
    </div>
  )
}
